/* -------------------------------------------------------------------------------------------------
Restricted - Copyright (C) Siemens Healthcare GmbH/Siemens Medical Solutions USA, Inc., 2021. All rights reserved
------------------------------------------------------------------------------------------------- */

import { css, html, LitElement } from 'lit-element';

/**
 * @prop {Boolean} unsortable - Defines if the head is sortable upon click or not.
 * @prop {Boolean} number - If set to `true`, the text will align to the right.
 * @prop {Boolean} icon - If set to `true`, the head will have a fixed width (2rem) and sorting won't apply.
 * @prop {Boolean} condensed - Adds condensed styles to head.
 * @prop {String} minWidth - If a value in px is defined, the head won't scale horizontally below that value, and horizontal scrolling may be applied.
 * @attr {String} min-width - If a value in px is defined, the head won't scale horizontally below that value, and horizontal scrolling may be applied.
 * @prop {Boolean} checkbox - 	If set to `true`, the cell will have a fixed width and checkbox can be placed inside to have it in the first column for checkbox multi-selection.
 * @slot Takes any component or text string as children.
 * @fires toggle - Dispatched when clicked on sortable head.
 * @fires head-ready - Dispatched when head has been first rendered.
 * @fires showarrow - Dispatched when sorting arrow is shown
 */
export class SHTableHead extends LitElement {
  static get properties() {
    return {
      /** @ignore */
      minWidth: { type: String, reflect: true, attribute: 'min-width' },
      /** @ignore */
      sortDescending: {
        type: Boolean,
        reflect: true,
        attribute: 'sort-descending',
      },
      icon: { type: Boolean, reflect: true },
      number: { type: Boolean, reflect: true },
      /** @ignore */
      columns: { type: String, reflect: true },
      condensed: { type: Boolean, reflect: true },
      unsortable: { type: Boolean, reflect: true },
      /** @ignore */
      colS: { type: String, reflect: true, attribute: 'col-s' },
      /** @ignore */
      colM: { type: String, reflect: true, attribute: 'col-m' },
      /** @ignore */
      sorted: { type: Boolean, reflect: true },
      checkbox: { type: Boolean, reflect: true },
    };
  }

  static get styles() {
    return [
      css`
        :host {
          display: inline-flex;
          box-sizing: border-box;
          width: -ms-fit-content;
          width: -moz-fit-content;
          outline: 0;
          align-items: center;
        }
        :host([unsortable][number]) {
          justify-content: flex-end;
        }
        :host * {
          box-sizing: border-box;
        }
        .head-wrapper {
          display: flex;
          color: var(--text-primary);
          font: var(--title-1);
          line-height: 2rem;
          margin: 0.5rem 0.75rem;
          height: calc(100% - 1rem);
          width: calc(100% - 1.5rem);
          -webkit-font-smoothing: antialiased !important;
          text-rendering: optimizeLegibility !important;
          -moz-osx-font-smoothing: grayscale !important;
        }
        :host(:not([icon]):not([checkbox])) .head-wrapper-text {
          width: fit-content;
          max-width: calc(100% - 2rem);
          white-space: nowrap;
          display: block;
          overflow: hidden;
          text-overflow: ellipsis;
        }
        :host(:not([checkbox])[unsortable]) .head-wrapper-text {
          max-width: 100%;
        }
        :host([active]) {
          background: transparent;
        }
        .sort-icon {
          display: inline-block;
          visibility: hidden;
          position: relative;
          color: rgba(var(--ui-1), var(--opacity-4));
        }
        .alwaysStay {
          visibility: visible;
        }
        /* number heads */
        :host([number]) .head-wrapper {
          display: flex;
          justify-content: flex-end;
        }
        /* icon heads */
        :host([icon]),
        :host([checkbox]) {
          flex: 0;
          min-width: 3.5rem !important;
        }
        :host([checkbox]) {
          margin-top: 0.2rem;
        }
        :host([icon]) .sort-icon,
        :host([checkbox]) .sort-icon {
          display: none;
        }
        /* medium screens */
        @media only screen and (max-width: 64rem) {
          :host([icon]),
          :host([checkbox]) {
            min-width: 3rem !important;
          }
          .head-wrapper {
            margin: 0.5rem;
            width: calc(100% - 1rem);
          }
        }
        /* hover */
        :host([unsortable]:hover:not(.no-hovermq)),
        :host([unsortable]:hover) *,
        :host([unsortable].no-hovermq):hover,
        :host([unsortable]):hover * {
          cursor: default;
        }
        :host([unsortable]:hover:not(.no-hovermq)) .head-wrapper,
        :host([unsortable].no-hovermq):hover .head-wrapper {
          cursor: default;
        }
        :host(:not(.no-hovermq):hover) .sort-icon,
        :host(.no-hovermq:hover) .sort-icon,
        :host(:not(.no-hovermq)) .head-wrapper.hover .sort-icon,
        :host(.no-hovermq) .head-wrapper.hover .sort-icon {
          visibility: visible;
          color: rgba(var(--ui-1), var(--opacity-3));
        }
        :host(:not(.no-hovermq)) .head-wrapper:hover,
        :host(.no-hovermq) .head-wrapper:hover,
        :host(:not(.no-hovermq)) .head-wrapper.hover,
        :host(.no-hovermq) .head-wrapper.hover,
        :host(:not(.no-hovermq):not([unsortable]):hover) {
          cursor: pointer;
        }
        /* Condensed Table */
        :host([condensed]) .head-wrapper {
          margin: 0.25rem 0.75rem;
          height: calc(100% - 0.5rem);
        }
        /* focus */
        .focus {
          outline: var(--focus-outline);
        }
      `,
    ];
  }

  render() {
    return html`
      <div class="head-wrapper">
        ${!this.unsortable && this.number
          ? html` <sh-icon
              icon="${this.sortDescending ? 'up-s' : 'down-s'}"
              class="sort-icon"
            ></sh-icon>`
          : ''}
        <div class="head-wrapper-text">
          <slot></slot>
        </div>
        ${!this.unsortable && !this.number
          ? html` <sh-icon
              icon="${this.sortDescending ? 'up-s' : 'down-s'}"
              class="sort-icon"
            ></sh-icon>`
          : ''}
      </div>
    `;
  }

  updated(changedProperties) {
    if (changedProperties.has('sorted')) {
      this.toggleArrowVisibility();
    }
    if (changedProperties.has('unsortable')) {
      this.unsortableChanged();
    }
    if (changedProperties.has('minWidth')) {
      this.adjustMinWidth();
    }
  }

  unsortableChanged() {
    if (this.unsortable) {
      this.sorted = false;
      this.sortDescending = false;
      this.tabindex = this.getAttribute('tabindex');
      this.removeAttribute('tabindex');
    } else {
      this.tabindex
        ? this.setAttribute('tabindex', this.tabindex)
        : this.setAttribute('tabindex', '0');
    }
  }

  constructor() {
    super();
    this.minWidth = '2.5rem';
    this.sorted = false;
  }

  connectedCallback() {
    super.connectedCallback();
    if (!this.icon && !this.hasAttribute('tabindex') && !this.unsortable) {
      this.setAttribute('tabindex', '0');
    }
  }

  firstUpdated() {
    this.addEventListener('click', () => this.handleClick());
    this.addEventListener('focus', () => this.handleFocus());
    this.addEventListener('blur', () => this.handleBlur());
    this.dispatchEvent(
      new CustomEvent('head-ready', {
        detail: this,
        bubbles: true,
      })
    );
  }

  adjustMinWidth() {
    if (this.minWidth !== undefined) {
      this.style.minWidth = this.minWidth;
    }
  }

  handleClick() {
    this.toggleIcon();
    if (!this.unsortable) {
      this.sortDescending = !this.sortDescending;
      this.sorted = true;
    }
  }

  handleFocus() {
    this.onkeyup = function (e) {
      const tabKey = 9;
      const enterKey = 13;
      const spaceKey = 32;
      if (e.keyCode === tabKey && !this.unsortable) {
        const wrapper = this.shadowRoot.querySelector('.head-wrapper');
        const icon = this.shadowRoot.querySelector('.sort-icon');
        wrapper.classList.add('focus');
        icon.style.visibility = 'visible';
      } else if (e.keyCode === enterKey || e.keyCode === spaceKey) {
        this.handleClick();
        this.toggleIcon();
      }
    };
  }

  handleBlur() {
    if (!this.unsortable) {
      const wrapper = this.shadowRoot.querySelector('.head-wrapper');
      const icon = this.shadowRoot.querySelector('.sort-icon');
      wrapper.classList.remove('focus');
      icon.style.visibility = '';
    }
  }

  /*Dispatch event for parent row to control sorted
    property of table-heads */
  toggleArrowVisibility() {
    if (this.sorted) {
      this.dispatchEvent(
        new CustomEvent('showarrow', {
          detail: this,
          bubbles: true,
          composed: true,
        })
      );
      this.shadowRoot.querySelector('.sort-icon').classList.add('alwaysStay');
    } else if (!this.unsortable) {
      this.sortDescending = false;
      this.shadowRoot.querySelector('.sort-icon').classList.remove('alwaysStay');
    }
  }

  toggleIcon() {
    if (!this.unsortable) {
      this.dispatchEvent(
        new CustomEvent('toggle', {
          detail: this,
          bubbles: true,
        })
      );
    }
  }
}

customElements.define('sh-table-head', SHTableHead);
